<template>
  <div class="part-card" :class="{ 'is-compact': compact, 'has-thumb': !letter && !!image }">
    <img v-if="!letter && image" class="part-card--thumb" :src="image" alt="" decoding="async">
    <div class="part-card--body">
    <div class="part-card--head">
      <span v-if="letter" class="part-card--letter" :class="'is-' + letter.toLowerCase()">{{ letter }}</span>
      <div class="part-card--name">
        <span class="part-card--brand">{{ specs.brand || eyebrow }}</span>
        <strong>{{ specs.model }}</strong>
      </div>
      <button v-if="!compact && describable" type="button" class="part-card--info" :aria-label="`Description of ${ specs.brand } ${ specs.model }`" @click="$emit('description')">
        <v-icon>mdi-information-outline</v-icon>
      </button>
    </div>
    <dl v-if="specs.stats.length" class="part-card--stats">
      <div v-for="stat in specs.stats" :key="stat.label">
        <dt>{{ stat.label }}</dt>
        <dd>{{ stat.value }}</dd>
      </div>
    </dl>
    <div v-if="!compact && ( expandable || specs.url )" class="part-card--actions">
      <!-- Wheels and tyres: the part pulled out of the bike with all its figures. -->
      <button v-if="expandable" type="button" class="part-card--more" @click="$emit( 'expand' )">
        <v-icon small>mdi-arrow-expand</v-icon>More info
      </button>
      <a v-if="specs.url" class="part-card--shop" :href="specs.url" target="_blank" rel="noopener">View in shop <span aria-hidden="true">↗</span></a>
    </div>
    </div>
  </div>
</template>

<script>
  export default {
    props: {
      // partSpecs() result: { brand, model, colour, url, stats }.
      specs: { type: Object, required: true },
      image: String,
      // Shown above the name when the part has no brand (e.g. a generic tyre).
      eyebrow: String,
      // 'A' / 'B' in compare mode, in place of the thumbnail.
      letter: String,
      compact: Boolean,
      describable: { type: Boolean, default: true },
      // Offer "More info" (the wheel / tyre spotlight).
      expandable: Boolean
    }
  };
</script>

<style lang="scss">
  .part-card {
    padding: 12px 14px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 14px; background: #fff;
    color: var(--builder-ink, #252c22); text-align: left;
  }
  .part-card--head { display: flex; align-items: center; gap: 12px; min-width: 0; }
  /* The part's photo large on the left, everything about it on the right. */
  .part-card.has-thumb { display: grid; grid-template-columns: 104px minmax(0, 1fr); gap: 12px; align-items: center; }
  .part-card--thumb { width: 104px; height: 88px; object-fit: contain; }
  .part-card--body { min-width: 0; }
  .part-card.has-thumb .part-card--stats { gap: 6px; }
  .part-card.has-thumb .part-card--stats dd { font-size: 13px; }
  .part-card--letter {
    flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px;
    border-radius: 50%; background: var(--builder-ink, #252c22); color: #fff; font-size: 11px; font-weight: 600;
  }
  .part-card--letter.is-b { background: var(--builder-accent, #344b35); }
  .part-card--name { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
  .part-card--brand { font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: #747c6a; line-height: 1.3; }
  .part-card--name strong { font-size: 17px; font-weight: 500; line-height: 1.25; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .part-card--info { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 50%; }
  .part-card--info .v-icon { font-size: 19px; color: var(--builder-accent, #344b35); }
  .part-card--stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin: 10px 0 0; }
  .part-card--stats dt { font-size: 11px; color: #747c6a; line-height: 1.3; }
  .part-card--stats dd { margin: 0; font-size: 14px; font-weight: 600; line-height: 1.3; font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .part-card--actions { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 8px; }
  .part-card--actions .part-card--shop { margin-top: 0; }
  .part-card--more {
    display: inline-flex; align-items: center; gap: 5px; height: 30px; padding: 0 12px; border-radius: 999px;
    background: var(--builder-accent, #344b35); color: #fff; font-size: 12px; letter-spacing: .02em;
  }
  .part-card--more .v-icon { color: inherit; font-size: 15px !important; }
  .part-card--more:focus-visible { outline: 2px solid var(--builder-accent, #344b35); outline-offset: 2px; }
  .part-card--shop { display: inline-block; margin-top: 8px; font-size: 13px; color: var(--builder-ink, #252c22) !important; text-decoration: underline; text-underline-offset: 3px; }
  .part-card.is-compact { padding: 10px 12px; border-radius: 12px; }
  .part-card.is-compact .part-card--name strong { font-size: 14px; white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .part-card.is-compact .part-card--stats { grid-template-columns: minmax(0, 1fr); gap: 2px; margin-top: 6px; }
  .part-card.is-compact .part-card--stats > div { display: flex; justify-content: space-between; gap: 6px; }
  .part-card.is-compact .part-card--stats dd { font-size: 12px; }
</style>
